import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { fireOperationalAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { IntegrationProviderMeta } from '../../../common/constants';
import type { ConnectionProviders } from '../../../common/types';
import { useConnectionStatus } from '../../../controllers/store';
import messages from './messages';

interface thirdPartyBannerProps {
    provider: ConnectionProviders;
}

export const ThirdPartyVsaBannerConnector = ({ provider }: thirdPartyBannerProps) => {
    di(useConnectionStatus, auth);
    const [{ data: connectionStatus }, { updateConnectionStatus }] = useConnectionStatus();
    const { formatMessage } = useIntl();
    const currentProviderStatus = connectionStatus[provider];
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleAuth = useCallback(
        (_, analyticsEvent: UIAnalyticsEvent) => {
            const href = currentProviderStatus?.outboundAuthUrl;
            if (!href) return;
            auth(href).then(
                () => {
                    updateConnectionStatus(provider);
                    fireOperationalAnalytics(analyticsEvent, '3pAuthConnection succeeded', { provider });
                },
                (err: Error) => {
                    const error = err ? err : new Error('Unknown Error Failed to connect');
                    fireOperationalAnalytics(analyticsEvent, '3pAuthConnection failed', {
                        provider,
                        error: error?.message,
                    });
                }
            );

            fireOperationalAnalytics(analyticsEvent, 'vsaThirdPartyBannerConnectAuth clicked', { provider });
        },
        [currentProviderStatus?.outboundAuthUrl, provider, updateConnectionStatus]
    );

    if (!connectionStatus[provider] || connectionStatus[provider]?.isUserOAuthed) return null;

    fireUIAnalytics(createAnalyticsEvent({}), 'vsaThirdPartyBanner viewed', { provider });

    return (
        <SectionMessage
            title={formatMessage(messages.vsaConnectorTitle, {
                provider: IntegrationProviderMeta[provider].name,
            })}
            appearance="warning"
            actions={[
                <SectionMessageAction key={messages.connectButton.id} onClick={handleAuth}>
                    {formatMessage(messages.connectButton, {
                        provider: IntegrationProviderMeta[provider].name,
                    })}
                </SectionMessageAction>,
            ]}
        >
            <p>{formatMessage(messages.vsaConnectorDescription)}</p>
        </SectionMessage>
    );
};
